import { SVGProps, useRef } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
	Box,
	BoxProps,
	Dialog,
	DialogProps,
	IconButton,
	Stack,
	Typography,
	styled,
	useTheme,
} from '@mui/material';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import ScrollShade from '~components/containers/scrollShade';
import { StatusVariant } from '~enums';
import { getStatusBackgroundColor } from '~enums/statusVariant';
import { useScrollShade } from '~hooks';
import i18n from '~lib/i18n';

import Dimens from '../../themes/dimens';

export interface CustomDialogProps extends DialogProps {
	/**
	 * The title of the dialog
	 */
	title?: string;
	titleAlignment?: 'left' | 'center' | 'right';
	subtitle?: string;
	/**
	 * A helper text to provide more information about
	 * the action.
	 */
	confirmButtonText?: string;
	abortButtonText?: string;
	/**
	 * Content shown next to the action buttons. When a string is provided it is
	 * rendered with the default hint formatting; any other React node is rendered as-is.
	 */
	buttonsHint?: React.ReactNode;
	loading?: boolean;
	disabled?: boolean;
	cancellable?: boolean;
	onConfirm?: () => void;
	/**
	 * The dialog is requested to be closed, by pressing the X button.
	 */
	onClose?: () => void;
	/**
	 * The dialog is triggered a decline, by pressing the cancel button.
	 */
	onDecline?: () => void;
	children?: React.ReactNode;
	showsCloseButton?: boolean;
	icon?: React.ReactElement<SVGProps<SVGSVGElement>>;
	variant?: StatusVariant;
	disableCloseOnBackdropClick?: boolean;
	allowContentOverflow?: boolean;
}

/**
 * Use a dialog to confirm a certain action
 *
 * TODO:
 * - Align close X properly
 *
 * @returns
 */
const CustomDialog = ({
	title = 'Dialog title',
	titleAlignment = 'center',
	subtitle,
	confirmButtonText: confirmText = i18n.t('confirm', { ns: 'actions' }),
	abortButtonText: abortText = i18n.t('cancel', { ns: 'actions' }),
	buttonsHint,
	loading = false,
	disabled = false,
	cancellable = true,
	variant = StatusVariant.Brand,
	onConfirm,
	onClose,
	onDecline,
	slots,
	icon,
	children,
	showsCloseButton = false,
	disableCloseOnBackdropClick = false,
	allowContentOverflow = false,
	...dialogProps
}: CustomDialogProps) => {
	const { palette } = useTheme();

	const contentRef = useRef<HTMLDivElement>(null);
	const shade = useScrollShade(contentRef);

	const handleConfirmClick = () => {
		onConfirm?.();
	};

	const handleDeclineClick = () => {
		onDecline?.();
	};

	const handleCloseClick = () => {
		onClose?.();
	};

	return (
		<Dialog
			{...dialogProps}
			onClose={(_, reason) => {
				if (reason === 'backdropClick' && disableCloseOnBackdropClick) return;

				handleCloseClick();
			}}
			slots={slots}
			sx={(theme) => ({
				'& .MuiDialog-paper': {
					borderRadius: Dimens.radius2xl,
					overflow: allowContentOverflow ? 'visible' : undefined,
					[theme.breakpoints.down('sm')]: {
						margin: Dimens.spacingXl,
						width: `calc(100% - ${Dimens.spacing3xl})`, //2 * ${Dimens.spacingXl}
					},
				},
			})}
		>
			{showsCloseButton ?
				<IconButton
					onClick={handleCloseClick}
					sx={{ position: 'absolute', top: Dimens.spacing3xl, right: Dimens.spacing3xl, zIndex: 1 }}
				>
					<CloseRoundedIcon />
				</IconButton>
			:	null}
			<Stack
				alignItems='center'
				sx={{
					boxSizing: 'border-box',
					...dialogProps.sx,
					minWidth: { xs: 'unset', sm: '380px' },
					width: { xs: '100%' },
					flex: 1,
					minHeight: 0,
					overflow: allowContentOverflow ? 'visible' : 'hidden',
					py: { xs: Dimens.spacingXl, sm: Dimens.spacingXl },
					px: { xs: Dimens.spacingXl, sm: Dimens.spacing3xl },
				}}
			>
				{icon ?
					<IconHolder variant={variant} sx={{ mb: Dimens.spacing3xl }}>
						{icon}
					</IconHolder>
				:	null}
				<Stack
					sx={{
						width: '100%',
						px: { xs: 0 },
						mb: Dimens.spacing2xl,
						flexShrink: 0,
					}}
				>
					<Typography variant='headingSm' align={titleAlignment} color={palette.textPrimary.main}>
						{title}
					</Typography>
					{subtitle && subtitle.length > 0 && (
						<Typography
							variant='paragraphSm'
							align={titleAlignment}
							color={palette.textQuaternary.main}
						>
							{subtitle}
						</Typography>
					)}
				</Stack>
				<Box
					ref={contentRef}
					sx={{
						width: '100%',
						flex: 1,
						minHeight: 0,
						overflow: allowContentOverflow ? 'visible' : undefined,
						overflowY: allowContentOverflow ? 'visible' : 'auto',
						scrollMargin: '12px',
						'&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari/Edge
						msOverflowStyle: 'none',
						scrollbarWidth: 'none', // Firefox
						scrollbarColor: 'transparent transparent',
						scrollbarGutter: 'stable',
					}}
				>
					{/* Scroll shadows drawn on top of the content (pointer-events: none),
					    so filled content at the edge can't hide them. */}
					{!allowContentOverflow && (
						<ScrollShade edge='top' visible={shade.top} surface={palette.background?.paper} />
					)}
					{children}
					{!allowContentOverflow && (
						<ScrollShade edge='bottom' visible={shade.bottom} surface={palette.background?.paper} />
					)}
				</Box>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={{ xs: Dimens.spacingLg, sm: Dimens.spacingMd }}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					sx={{
						width: '100%',
						mt: Dimens.spacing3xl,
						flexShrink: 0,
					}}
				>
					{typeof buttonsHint === 'string' ?
						buttonsHint.length > 0 && (
							<Typography
								variant='labelSm'
								color={palette.textQuaternary.main}
								sx={{ width: '100%', flex: { sm: 1 } }}
							>
								{buttonsHint}
							</Typography>
						)
					: buttonsHint ?
						<Box sx={{ width: '100%', flex: { sm: 1 } }}>{buttonsHint}</Box>
					:	null}
					<Stack
						direction={{ xs: 'column-reverse', sm: 'row' }}
						spacing={{ xs: Dimens.spacingLg, sm: Dimens.spacingMd }}
						alignItems='center'
						sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}
					>
						{cancellable && (
							<ActionButton
								label={abortText}
								onClick={handleDeclineClick}
								sx={{
									flex: 1,
									width: { xs: '100%', sm: 'auto' },
								}}
								style={ActionButtonStyle.Bordered}
							/>
						)}
						<ActionButton
							loading={loading}
							variant={variant}
							disabled={disabled || loading}
							onClick={handleConfirmClick}
							label={confirmText}
							sx={{
								flex: 1,
								width: { xs: '100%', sm: 'auto' },
							}}
						/>
					</Stack>
				</Stack>
			</Stack>
		</Dialog>
	);
};

interface IconHolderProps extends BoxProps {
	variant: StatusVariant;
}

const IconHolder = styled(Box)<IconHolderProps>((props) => {
	return {
		display: 'flex',
		height: '64px',
		width: '64px',
		borderRadius: Dimens.radiusFull,
		backgroundColor: getStatusBackgroundColor(props.variant, props.theme.palette),
		justifyContent: 'center',
		alignItems: 'center',
		'& svg': {
			height: '24px',
			width: '24px',
			'& path': {
				stroke:
					props.variant == StatusVariant.Destructive ?
						props.theme.palette.bgErrorSolid.main
					:	props.theme.palette.bgSuccessSolid.main,
			},
		},
	};
});

export default CustomDialog;
